/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, May 9th 2022, 12:16:01 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import * as uuid from "uuid";
import {
  Portal,
  ProcessDefinition,
  ProcessStage,
  TaskTemplate,
  NextActivation,
  ActivationCondition,
  BillingSchedule,
  BillingConfiguration,
  DatasetReference,
  DataBank,
  DatasetSchema,
  NotificationTemplate,
  SEND_WHEN_OPTIONS,
  TARGET_USERS,
  ICondition,
  FormConfiguration,
  FORM_STATUS_CHOICES_ENUM,
  FIELD_TYPES,
  FormField,
  ChoiceOption,
  ChoiceOptionGroup,
  FieldDependency,
  FieldDependencyConditionGroup,
  FieldDependencyCondition,
  FieldDependencyAction,
  FieldValidator,
  NumberingSequence,
  AjaxSource,
  AjaxSourceParameter,
  Organization,
  User,
  Team,
  WebsiteMenu,
  ProcessTemplateCategory,
  DesignLayout,
  IntegrationCredentialStore,
  IntegrationConnection,
  IntegrationProvider,
  AITaskInstruction,
  ContentTag,
} from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import { getRandomWholeNumber, convertTextToInitials } from "lib/utils";

interface ProcessDefinitionFactoryArgs {
  organizationId: ProcessDefinition["artifact_org"];
}

export const getDefaultProcessDefinition = ({ organizationId }: ProcessDefinitionFactoryArgs): ProcessDefinition => ({
  title: "",
  service_code: "",
  service_unit: "Submission",
  sequence: 0,
  site: null, // Added by user when enterting general info.
  start_event: "form_sub",
  status: "DEVELOPMENT",
  submission_forms: [],
  artifact_org: organizationId,
});

interface ProcessTemplateCategoryFactoryArgs {
  organizationId: ProcessTemplateCategory["artifact_org"];
  site: ProcessTemplateCategory["site"];
}

export const getDefaultProcessCategory = ({
  organizationId,
  site,
}: ProcessTemplateCategoryFactoryArgs): ProcessTemplateCategory => ({
  title: "",
  sequence: 0,
  active: true,
  site,
  artifact_org: organizationId,
});

interface TaskTemplateFactoryArgs {
  processDefinition: ProcessDefinition;
  bpmnNodeType: TaskTemplate["type"];
  defaultSequence: number;
  title?: string;
  optionalProperties?: Partial<TaskTemplate>;
}

export const getDefaultTaskTemplate = ({
  processDefinition,
  bpmnNodeType,
  defaultSequence,
  title = "New Task",
  optionalProperties,
}: TaskTemplateFactoryArgs): TaskTemplate => ({
  process: processDefinition.id,
  title,
  type: bpmnNodeType,
  next: null,
  sequence: defaultSequence,
  enabled: true,
  client_task: false,
  assign_task: false,
  cancel_active: false,
  generate_document: false,
  update_linked_entries: false,
  default_show_form: true,
  can_self_assign: false,
  artifact_org: processDefinition.artifact_org,
  maximum_duration: 0,
  ...optionalProperties,
});

interface GetNewProcessStageDefaultArgs {
  processDefinition: ProcessDefinition;
  sequence: number;
}

export const getNewProcessStageDefault = ({
  processDefinition,
  sequence,
}: GetNewProcessStageDefaultArgs): ProcessStage => ({
  stage_name: null,
  stage_type: "default",
  active: true,
  is_landing: false,
  progress_percentage: 0,
  process: processDefinition.id,
  artifact_org: processDefinition.artifact_org,
  sequence,
});

export const getNewActivationDefault = ({
  organizationId,
  taskTemplateId,
  otherProperties,
}: {
  organizationId: NextActivation["artifact_org"];
  taskTemplateId: NextActivation["task_definition"];
  otherProperties?: Omit<Partial<NextActivation>, "artifact_org" | "task_definition">;
}): NextActivation => ({
  description: "New Decision Branch",
  task_definition: taskTemplateId,
  artifact_org: organizationId,
  ontrue: null,
  ...otherProperties,
});

export const getDefaultActivationCondition = (nextActivation: NextActivation): ActivationCondition => ({
  activation: nextActivation.id,
  artifact_org: nextActivation.artifact_org,
  condition_form: null,
  condition_field: null,
  condition_operator: null,
  condition_value: null,
});

export const getNewBillingScheduleDefault = (
  organizationId: PrimaryKeyValue<Organization>,
  billingConfigurationId: PrimaryKeyValue<BillingConfiguration>
): BillingSchedule => ({
  artifact_org: organizationId,
  code: `Fee-${getRandomWholeNumber()}`,
  billing_configuration: billingConfigurationId,
  name: `New Fee`,
  price_settings: {},
  reviewers_can_edit_price: false,
});

interface getNewNotificationTemplateDefaultArgs {
  organizationId: PrimaryKeyValue<Organization>;
  taskTemplateId: TaskTemplate["id"];
  taskTemplateTitle: TaskTemplate["title"];
  taskTemplateType: TaskTemplate["type"];
  taskTemplateIsClientTask: TaskTemplate["client_task"];
}

export const getNewNotificationTemplateDefault = ({
  organizationId,
  taskTemplateId,
  taskTemplateTitle,
  taskTemplateType,
  taskTemplateIsClientTask,
}: getNewNotificationTemplateDefaultArgs): NotificationTemplate => {
  let title: string;
  let message: string;
  let send_when: keyof typeof SEND_WHEN_OPTIONS;
  let target_users: keyof typeof TARGET_USERS;

  switch (taskTemplateType) {
    case "View":
      title = "New '{{task_name}}' Task.";
      message =
        '<div>{% block email_content %}</div><div>Hello,<br></div><div>You have a new task available.<br></div><div>{% endblock %}</div><div><br></div><div>{% block email_buttons %}<a class="btn btn-primary" href="{{task_url}}">{{task_name}}</a>{% endblock %}</div>';
      send_when = "task_review";
      target_users = taskTemplateIsClientTask ? "applicant" : "staff";
      break;
    default:
      title = "'{{application_reference}}' Updated.";
      message =
        '<div>{% block email_content %}</div><div>Hello,<br></div><div>Submission {{application_reference}} has been updated.<br></div><div>{% endblock %}</div><div><br></div><div>{% block email_buttons %}<a class="btn btn-primary" href="{{application_public_link}}">View Submission Details</a>{% endblock %}</div>';
      send_when = "task_done";
      target_users = "applicant";
  }

  return {
    title,
    message,
    send_when,
    target_users,
    artifact_org: organizationId,
    task_template: taskTemplateId,
    enabled: true,
    type: "email",
  };
};

export const getNewConditionDefault = (): ICondition => ({
  condition_field: "",
  condition_operator: "is_equal",
  condition_value: "",
});

export const getDefaultDatasetReference = (organizationId: DatasetReference["artifact_org"]): DatasetReference => ({
  name: "",
  ref_metadata: { pk: "id", json_dataset_schema: null },
  artifact_org: organizationId,
});

export const getDefaultDataBank = (organizationId: DataBank["artifact_org"]): DataBank => ({
  name: "",
  artifact_org: organizationId,
});

export const getDefaultDatasetSchema = (
  organizationId: DatasetSchema["artifact_org"],
  data_bank: DatasetSchema["data_bank"]
): DatasetSchema => ({
  json_schema: {
    title: "New",
    type: "object",
    properties: {},
    maxProperties: 0,
    required: [],
    additionalProperties: false,
  },
  data_bank,
  artifact_org: organizationId,
});

export const getDefaultAjaxSourceParameter = (ajaxSource: AjaxSource): AjaxSourceParameter => ({
  key: null,
  value: null,
  source: ajaxSource?.id ?? null,
  sequence: 0,
  artifact_org: ajaxSource.artifact_org,
  artifact_guid: uuid.v4(),
});

export const getDefaultAjaxSource = (organization: Organization["id"]): AjaxSource => ({
  name: null,
  source: null,
  request_method: "GET",
  resource_type: "http",
  soap_call: "",
  source_parameters: [],
  artifact_org: organization,
  artifact_guid: uuid.v4(),
});

export const getFormConfiguration = (
  organizationId: Organization["id"],
  options: Omit<FormConfiguration, "artifact_org" | "artifact_guid" | "artifact_tag" | "status"> &
    Partial<Pick<FormConfiguration, "status">>
): FormConfiguration => {
  const { form_type } = options;
  let button_text: string = "Submit";
  let enforce_editable_fields_on_resubmission: boolean = true;

  switch (form_type) {
    case "registration_form":
      button_text = "Register";
      break;
    case "payment_form":
      button_text = "Pay";
      break;
    case "review_form":
      enforce_editable_fields_on_resubmission = false;
      break;
    default:
      button_text = "Submit";
  }

  return {
    artifact_org: organizationId,
    status: FORM_STATUS_CHOICES_ENUM.Published,
    button_text,
    enforce_editable_fields_on_resubmission,
    ...options,
  };
};

const fieldDefaults = {
  getDefaultExtraConfigurations: (fieldType: FIELD_TYPES): FormField["extra_configurations"] => {
    switch (fieldType) {
      case FIELD_TYPES.GEO_COORDINATES:
        return {
          geo_field_settings: {
            show_map: true,
            default_latitude: -1.2921,
            default_longitude: 36.8219,
            default_to_current_location: false,
          },
        };
      default:
        return {};
    }
  },
  defaultFieldIsRequired: (defaultFieldType: FIELD_TYPES) =>
    ![
      FIELD_TYPES.SECTION_BREAK,
      FIELD_TYPES.PAGE_TITLE,
      FIELD_TYPES.TITLE,
      FIELD_TYPES.HIDDEN,
      FIELD_TYPES.AUTO_GENERATED_SEQUENCE,
    ].includes(defaultFieldType),
  defaultFieldIsUnique: (defaultFieldType: FIELD_TYPES) =>
    [FIELD_TYPES.AUTO_GENERATED_SEQUENCE].includes(defaultFieldType),
  defaultFieldIsEdtitable: (defaultFieldType: FIELD_TYPES) =>
    ![FIELD_TYPES.AUTO_GENERATED_SEQUENCE].includes(defaultFieldType),
};

export const getDefaultField = (
  form: FormConfiguration,
  defaultFieldType: FIELD_TYPES,
  defaultOrder: number,
  label: string
): FormField => ({
  label,
  field_type: defaultFieldType,
  order: defaultOrder,
  required: fieldDefaults.defaultFieldIsRequired(defaultFieldType),
  required_for_draft: false,
  visible: true,
  unique_key: fieldDefaults.defaultFieldIsUnique(defaultFieldType),
  form: form.id,
  editable: fieldDefaults.defaultFieldIsEdtitable(defaultFieldType),
  extra_configurations: fieldDefaults.getDefaultExtraConfigurations(defaultFieldType),
  artifact_org: form.artifact_org,
  char_length: null,
  slug: null,
});

export const getDefaultChoiceOption = (
  optionGroup: ChoiceOptionGroup,
  option_label: string,
  option_value: string,
  sequence: number
): ChoiceOption => ({
  option_label,
  option_value,
  sequence,
  option_group: optionGroup.id,
  artifact_org: optionGroup.artifact_org,
});

type getDefaultFieldDependencyArgs = { form: FormConfiguration };

export const getDefaultFieldDependency = ({ form }: getDefaultFieldDependencyArgs): FieldDependency => ({
  form: form.id,
  condition_logic_type: "all",
  get field_dependency_conditions() {
    return [getDefaultFieldDependencyCondition({ fieldDependency: this })];
  },
  get field_dependency_actions() {
    return [getDefaultFieldDependencyAction({ fieldDependency: this })];
  },
  field_dependency_condition_groups: [],
  artifact_org: form.artifact_org,
});

type dependencyConditionGroupArgs = { fieldDependency: FieldDependency };

export const getDefaultFieldDependencyConditionGroup = ({
  fieldDependency,
}: dependencyConditionGroupArgs): FieldDependencyConditionGroup => ({
  id: uuid.v4(),
  artifact_guid: uuid.v4(),
  condition_logic_type: "any",
  artifact_org: fieldDependency?.artifact_org,
});

type dependencyConditionArgs = { fieldDependency: FieldDependency; groupId?: FieldDependencyConditionGroup["id"] };

export const getDefaultFieldDependencyCondition = ({
  fieldDependency,
  groupId = null,
}: dependencyConditionArgs): FieldDependencyCondition => ({
  artifact_guid: uuid.v4(),
  dependency: fieldDependency?.id,
  condition_form: fieldDependency?.form,
  condition_field: null,
  condition_operator: "is_equal",
  condition_value: null,
  artifact_org: fieldDependency?.artifact_org,
  condition_group: groupId,
});

type dependencyActionArgs = { fieldDependency: FieldDependency };

export const getDefaultFieldDependencyAction = ({ fieldDependency }: dependencyActionArgs): FieldDependencyAction => ({
  artifact_guid: uuid.v4(),
  dependency: fieldDependency?.id,
  action_permitted_values: [],
  dependent_field: null,
  dependent_field_action: "show",
  artifact_org: fieldDependency?.artifact_org,
});

export const getDefaultPermittedValue = (): FieldDependencyAction["action_permitted_values"][number] => ({
  id: uuid.v4(),
  value: null,
});

export const getDefaultFieldValidation = ({
  organizationId,
}: {
  organizationId: Organization["id"];
}): FieldValidator => ({
  is_global: false,
  name: "",
  type: "dataset",
  dataset_lookup_settings: { lookup_column: null },
  artifact_org: organizationId,
});

interface GetDefaultNumberingSequenceArgs {
  organizationId: PrimaryKeyValue<Organization>;
  objectName: string;
  defaultPrefix?: string;
  optionalProperties?: Partial<NumberingSequence>;
}
export const getDefaultNumberingSequence = ({
  objectName,
  organizationId,
  defaultPrefix,
  optionalProperties,
}: GetDefaultNumberingSequenceArgs): NumberingSequence => {
  const nameInitials = convertTextToInitials(objectName).join("");
  return {
    artifact_org: organizationId,
    active: true,
    name: `Numbering Sequence for ${objectName}`,
    prefix: defaultPrefix ?? `${nameInitials}`,
    code: `${nameInitials}`,
    padding: 2,
    ...optionalProperties,
  };
};

export const getNewDefaultTeam = (organization: Organization["id"], members: User["id"][]): Team => ({
  organization,
  members,
  team_name: null,
  permissions: [],
  group: null,
});

export const getDefaultWebsiteMenu = (
  portalId: Portal["id"],
  sequence: WebsiteMenu["sequence"] = null
): WebsiteMenu => ({
  sequence,
  name: "",
  new_window: false,
  site: portalId,
});

export const getDefaultDesignLayout = ({
  layout_type,
  organizationId,
}: Pick<DesignLayout, "layout_type"> & { organizationId: DesignLayout["artifact_org"] }): DesignLayout => {
  const defaultCSSClass = `${layout_type}-custom-layout`;
  return {
    title: `Layout: ${uuid.v4()}`,
    active: true,
    css: `.${defaultCSSClass} {}`,
    html_design: `<div class=${defaultCSSClass}>Design Your Layout</div>`,
    layout_type,
    artifact_org: organizationId,
  };
};

export const getDefaultIntegrationConnection = (options: {
  organization: Organization;
  provider: IntegrationProvider;
  defaults: Partial<IntegrationConnection>;
}): IntegrationConnection => ({
  name: `${options?.provider?.name} Connection`,
  integration_code: options?.provider?.code,
  integration_actions: [],
  status: "active",
  credentials_store: null,
  integration_task_templates: [],
  organization: options?.organization?.id,
  ...options.defaults,
});

export const getDefaultCredentialStore = (options: {
  organization: Organization;
  defaultName: string;
}): IntegrationCredentialStore => ({
  name: options?.defaultName,
  credentials: null,
  organization: options?.organization?.id,
});

export const getDefaultAITaskInstruction = (
  organization: Organization,
  defaults?: Partial<Omit<AITaskInstruction, "id" | "provider_code" | "artifact_org">>
): AITaskInstruction => ({
  name: "New AI Task Instruction",
  prompt: "Review the Submission.....",
  provider_code: "openai",
  artifact_org: organization.id,
  ...defaults,
});

export const getDefaultContentTag = (organizationId: Organization["id"]): ContentTag => ({
  name: "",
  slug: "",
  description: null,
  organization: organizationId,
});
